import { classNamesFunction, FontWeights, Stack } from '@fluentui/react';
import { IImageStyleProps, IImageStyles, Image } from '@fluentui/react/lib/Image';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import { AzureMLTabConnectionFooter } from 'src/customText/verticals/azureMLConnectionModal/components/AzureMLTabConnectionFooter';
import styled from 'styled-components';

type OverviewPageProps = {
    onNext: () => void;
    onDismiss: () => void;
};

const messages = defineMessages({
    title: {
        id: 'customText.overviewPage.title',
        defaultMessage: `Connect your labeling project to
        Azure Machine Learning `
    },
    section1: {
        id: 'customText.overviewPage.section1',
        defaultMessage: `You can now label the same dataset using both`
    },
    languageStudio: {
        id: 'customText.overviewPage.languageStudio',
        defaultMessage: `Language Studio`
    },
    or: {
        id: 'customText.overviewPage.or',
        defaultMessage: `or`
    },
    azureML: {
        id: 'customText.overviewPage.azureML',
        defaultMessage: `Azure Machine Learning`
    },
    section2: {
        id: 'customText.overviewPage.section2',
        defaultMessage: `by connecting the labeling project.`
    },
    question1: {
        id: 'customText.overviewPage.question1',
        defaultMessage: 'Why connect to data labeling in Azure Machine Learning?'
    },
    answer1: {
        id: 'customText.overviewPage.answer1',
        defaultMessage: 'You might want to connect your project because you:'
    },
    listItem1: {
        id: 'customText.overviewPage.listItem1',
        defaultMessage: 'Already have labeled data in Azure Machine Learning that you want to bring in to the Language Studio'
    },
    listItem2: {
        id: 'customText.overviewPage.listItem2',
        defaultMessage: 'Want to use outside vendor labeling'
    },
    listItem3: {
        id: 'customText.overviewPage.listItem3',
        defaultMessage: 'Need multiple users to label or review the same data sets'
    },
    question2: {
        id: 'customText.overviewPage.question2',
        defaultMessage: 'What do I need to connect?'
    },
    answer2: {
        id: 'customText.overviewPage.answer2',
        defaultMessage:
            'You’ll need an Azure Machine Learning account with a pricing plan that can accommodate the compute needs of your project. You’ll also need to understand the limitations, such as documents labeled as test or training data will not retain that label after connecting.'
    },
    learnMore: {
        id: 'customText.overviewPage.learnMore',
        defaultMessage: 'Learn more about connected labeling experiences'
    }
});

const stepTheme = getScopedTheme('OverviewPage');
const Wrapper = styled.div`
    width: 90%;
    padding: 23px;
    background: #cde0f1;
    display: flex;
    flex-direction: column;
    align-items: center;
`;
const CenteredText = styled(LuisText)({ textAlign: 'center' });
const imageStyles = classNamesFunction<IImageStyleProps, IImageStyles>()({
    image: { width: '100%', height: 'auto' }
});

export const OverviewPage = (props: OverviewPageProps) => {
    const { onNext, onDismiss } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();

    return (
        <Stack styles={{ root: { minHeight: '620px' } }}>
            <Stack
                styles={{ root: { maxHeight: '535px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 12, padding: `${isLargeOrGreater ? `0 0 0 32px` : '12px 20px'}` }}>
                <LuisText size="32px" color="#136BFB">
                    {localize(messages.title)}
                </LuisText>
                <Wrapper>
                    <Image styles={imageStyles} src={getAssetsUrl('assets/images/customText/azureMLConnection.svg')} />
                    <CenteredText>
                        <LuisText variant="mediumPlus">{localize(messages.section1)}</LuisText>
                        &nbsp;
                        <LuisText variant="mediumPlus" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {localize(messages.languageStudio)}
                        </LuisText>
                        &nbsp;
                        <LuisText variant="mediumPlus">{localize(messages.or)}</LuisText>
                        &nbsp;
                        <LuisText variant="mediumPlus" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {localize(messages.azureML)}
                        </LuisText>
                        &nbsp;
                        <LuisText variant="mediumPlus">{localize(messages.section2)}</LuisText>
                    </CenteredText>
                </Wrapper>
                <LuisText variant="mediumPlus" fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.question1)}
                </LuisText>
                <LuisText>{localize(messages.answer1)}</LuisText>
                <ul style={{ marginLeft: '50px' }}>
                    <li>
                        <LuisText>{localize(messages.listItem1)}</LuisText>
                    </li>
                    <li>
                        <LuisText>{localize(messages.listItem2)}</LuisText>
                    </li>
                    <li>
                        <LuisText>{localize(messages.listItem3)}</LuisText>
                    </li>
                </ul>
                <LuisText variant="mediumPlus" fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.question2)}
                </LuisText>
                <LuisText>
                    {localize(messages.answer2)}
                    &nbsp;
                    <ExternalLink styles={{ root: { color: stepTheme.calloutLinkColor } }} href={``}>
                        {localize(messages.learnMore)}
                    </ExternalLink>
                </LuisText>
            </Stack>

            <AzureMLTabConnectionFooter onNext={onNext} onDismiss={onDismiss} activeStep={AMLConnectionSteps.Overview} />
        </Stack>
    );
};
